import React, { useContext, MouseEvent } from 'react';
import { StyledHighlightedWord } from '../Styled';
import { StyledWordContext } from '../../contexts/styledWordContext';
import { ExemplarWord } from '../../types';
import { EXEMPLAR_MODES } from '../../constants';
import { getLangugeToolCoordinates, isWordUnderlined } from '../../utils';

interface IClickableWord {
  page: string;
  languageHighlighted: boolean;
  word: ExemplarWord;
  handleWordHighlight: (word: ExemplarWord) => void;
  saveHighlightsUpdates: (prevState: ExemplarWord, state: ExemplarWord) => void;
  handleTool: (isVisible: boolean, word: ExemplarWord, x: number, y: number) => void;
  handleFontStyle?: (word: ExemplarWord) => void;
  editMode: boolean;
  isClickable: boolean;
}

export const CLICKABLE_WORD = 'CLICKABLE_WORD';

export const stylesToHighlightLangWord = ['shadow', 'borderRadius', 'bg'];
export const stylesToHighlightNoLangWord = ['shadow', 'borderRadius'];

export const getHighlightStyles = (defaultStyles: string[] | undefined, stylesToConcat: string[]): string[] =>
  defaultStyles && defaultStyles.length ? defaultStyles.concat(stylesToConcat) : stylesToConcat;

export const getNoHighlightStyles = (highlightStyles: string[] | undefined, stylesToRemove: string[]): string[] =>
  highlightStyles ? highlightStyles.filter(style => !stylesToRemove.includes(style)) : [];

export const ClickableWord: React.FC<IClickableWord> = (props): JSX.Element => {
  const {
    page,
    children,
    handleWordHighlight,
    saveHighlightsUpdates,
    languageHighlighted,
    word,
    handleTool,
    handleFontStyle,
    editMode,
    isClickable,
  } = props;

  const { styledWordState } = useContext(StyledWordContext);

  const highlightedWord = { color: word.highlight, highlighted: word.highlight != undefined };
  const highlighted = !!(!styledWordState.isHideHighlightsMode && highlightedWord?.highlighted && !languageHighlighted);
  const isHidden = styledWordState.isHiddenTextMode;

  const handleWordClick = (event: MouseEvent<HTMLSpanElement>) => {
    if (!editMode) {
      return;
    }

    if (styledWordState.isHighlightingMode) {
      if (highlighted && highlightedWord.color === styledWordState.color) {
        const { highlight, ...nonHighlightedWord } = word;

        handleWordHighlight(nonHighlightedWord);
        saveHighlightsUpdates(word, nonHighlightedWord);
      } else {
        const state = { ...word, highlight: styledWordState.color };

        handleWordHighlight(state);
        saveHighlightsUpdates(word, state);
      }
    }
    if (styledWordState.isFontMode && !styledWordState.isHighlightingMode && !styledWordState.isHideHighlightsMode) {
      handleTools(event);
    }
  };

  const handleTools = (event: MouseEvent<HTMLSpanElement>) => {
    if (page !== EXEMPLAR_MODES.languageFeatures) {
      return;
    }

    setTooltipAndStyles(event);
  };

  const setTooltipAndStyles = (event: MouseEvent<HTMLSpanElement>) => {
    if (!handleFontStyle) return;

    const wordSpan = event.target as HTMLSpanElement;

    const { x, y } = getLangugeToolCoordinates(wordSpan);

    const { style } = word;
    let isVisible;

    const highlightStyles = getHighlightStyles(word.style, stylesToHighlightNoLangWord);

    if (styledWordState.tool.word.id === word.id) {
      isVisible = !styledWordState.tool.isVisible;

      const noHighlightStyles = getNoHighlightStyles(style, stylesToHighlightNoLangWord);

      isVisible
        ? handleFontStyle({ ...word, style: highlightStyles })
        : handleFontStyle({ ...styledWordState.tool.word, style: noHighlightStyles });

      handleTool(isVisible, styledWordState.tool.word, x, y);
    } else {
      const noHighlightStyles = getNoHighlightStyles(styledWordState.tool.word.style, stylesToHighlightNoLangWord);

      styledWordState.tool.word.id && handleFontStyle({ ...styledWordState.tool.word, style: noHighlightStyles });

      isVisible = true;

      handleFontStyle({ ...word, style: highlightStyles });

      const { ...filteredWord } = word;

      handleTool(isVisible, filteredWord as ExemplarWord, x, y);
    }
  };

  return (
    <StyledHighlightedWord
      data-testid="clickable-word"
      isClickable={isClickable}
      isHidden={isHidden}
      isUnderlined={isWordUnderlined(word.text_type, page, styledWordState.isUnderlineHiddenMode)}
      onClick={handleWordClick}
      color={highlightedWord?.color}
      highlighted={highlighted}
      className={CLICKABLE_WORD}
    >
      {children}
    </StyledHighlightedWord>
  );
};
